/**
 * The funnel panel type and the settings it draws from.
 */

const funnel = {
  names: [
    {
      id: 'funnel',
      label: 'Funnel',
      phrase: 'a funnel',
      guide: 'funnel.md',
      advanced: true,
      toolSummary: 'how many survive each step of a sequence — one row per step, in step order, capped at 12',
      promptSummary: 'how many survive each step of a sequence and where they drop out — one row per step in step order, up to 12; the value is cumulative, so a stage-by-stage snapshot is a bar chart instead'
    }
  ],
  block: 'funnel',
  /* AnalyticsFunnelPanel fires stageClick with the stage's SOURCE ROW. */
  events: ['stageClick'],
  /* Funnel. The query already returns one row per step, in step order; this block identifies the step's
     name and the number that reached it. `orientation` selects the flow direction. `percentBase`
     selects which percentage the panel puts in the flow. There is no field for the order or band
     thickness: the order is the rows' own, and the thickness is geometry. */
  fields: {
    categoryKey: { kind: 'string', required: true, hint: 'a step needs a name and the number that reached it', label: 'Step label' },
    valueKey: { kind: 'string', required: true, hint: 'a step needs a name and the number that reached it', label: 'Reached' },
    orientation: { kind: 'enum', values: ['horizontal', 'vertical'], default: 'horizontal', label: 'Orientation', ariaLabel: 'Funnel orientation', valueLabels: { horizontal: 'Horizontal', vertical: 'Vertical' } },
    percentBase: { kind: 'enum', values: ['first', 'previous'], default: 'first', label: 'Percentage', ariaLabel: 'Which percentage each bar leads with', valueLabels: { first: 'Of first step', previous: 'Of step before' } }
  },
  editorHint: 'One row is one step, drawn in the order the rows arrive. Horizontal runs left to right; vertical runs top to bottom with its stage cards on the right. The value is the count that got at least this far, not the count sitting in that stage today, so build the running total in SQL when the source only reports each stage separately.',
  /* The percentage base is named because it decides what every number on the panel MEANS, so the
     assistant can see a funnel is already reading against the previous step before it changes
     anything else. */
  stateSummary: ({ block }) => (block.categoryKey && block.valueKey ? `funnel: ${block.categoryKey} × ${block.valueKey}, percent of ${block.percentBase === 'previous' ? 'previous step' : 'first step'}, ${block.orientation === 'vertical' ? 'vertical' : 'horizontal'}` : ''),
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: false,
    required: ['categoryKey', 'valueKey'],
    description: 'Funnel mapping (displayType "funnel"): ONE ROW IS ONE STEP of a sequence, and THE ROW ORDER IS THE STEP ORDER — the panel never reorders, so the query must return the steps top to bottom. The value is CUMULATIVE: how many reached at least that step, not how many sit in that stage now. A stage-by-stage snapshot is a bar chart, not a funnel; when the source has no stage history, build the running total in SQL. Capped at 12 steps, with anything not drawn counted below the bars. Read immutable/analytics/panel-types/funnel.md before configuring one.',
    properties: {
      categoryKey: { type: 'string', description: 'Column naming the step. Dot paths work.' },
      valueKey: { type: 'string', description: 'Column holding the number that reached that step. Dot paths work. Zero is drawn (the step where the pipeline ran dry); an empty, non-numeric, or negative count is counted as not shown.' },
      orientation: { type: 'string', enum: ['horizontal', 'vertical'], description: 'How the funnel flows. "horizontal" (default) runs left to right with stage cards below. "vertical" runs top to bottom with stage cards on the right.' },
      percentBase: {
        type: 'string',
        enum: ['first', 'previous'],
        description: 'Which percentage each stage shows. "first" (default) is the share of the first step, for "what fraction of everyone who entered got this far". "previous" is the share of the step above, for "where are we losing people" — a low number points straight at the step that leaks.'
      }
    }
  }
};

export default funnel;
